/* eslint-disable @next/next/no-img-element */
import useTranslation from 'next-translate/useTranslation';
import Link from 'next/link';

// Hooks
import useWebflowArticles from '@/utils/hooks/swr/useWebflowArticles';

// Types
import type { PlatformLanguagesType } from '@/types/userType';
import type { WebflowArticle } from '@/utils/hooks/swr/useWebflowArticles';

const MAX_ARTICLES = 3;

const BlogPost = ({ title, content, image_url, link }: Omit<WebflowArticle, 'date' | 'type'>) => {
    const { t } = useTranslation('home');

    return (
        <Link
            className="relative flex flex-col justify-end rounded-md border-4 border-white bg-white dark:border-icoDarkMode-anthracite dark:bg-icoDarkMode-anthracite"
            href={link}
            target="_blank"
            data-testid="blog-post">
            {image_url ? (
                <div className="relative h-36 w-full overflow-hidden rounded">
                    <img src={image_url} alt={title} className="h-full w-full object-cover" />
                </div>
            ) : null}
            <div className="mx-4 my-5 flex flex-col justify-end space-y-2.5">
                <p
                    className="line-clamp-2 font-GBold text-xl text-icoBlack dark:text-white"
                    title={t(title)}>
                    {t(title)}
                </p>
                <p
                    className="line-clamp-3 font-GMedium text-sm text-icoGray-500 dark:text-icoDarkMode-wolf"
                    title={t(content)}>
                    {t(content)}
                </p>
            </div>
        </Link>
    );
};

const BlogPosts = () => {
    const { lang } = useTranslation('home');

    const { articles, articlesError, isLoading } = useWebflowArticles({
        params: {
            article_types: ['blog'],
            article_count: MAX_ARTICLES,
            lang: lang as PlatformLanguagesType
        }
    });

    if (isLoading)
        return [...Array(MAX_ARTICLES)].map((_, index) => <LoaderSkeleton key={index} />);

    if (articlesError) return null;

    return articles.map(({ title, content, image_url, link }) => (
        <BlogPost key={title} title={title} content={content} image_url={image_url} link={link} />
    ));
};

const LoaderSkeleton = () => (
    <div
        className="relative rounded-md border-4 border-white bg-white dark:border-icoDarkMode-anthracite dark:bg-icoDarkMode-anthracite"
        data-testid="loader-skeleton">
        <div className="h-36 w-full animate-pulse rounded bg-icoGray-100 dark:bg-icoDarkMode-deepBlack"></div>
        <div className="mx-4 my-5 flex flex-col space-y-2.5">
            <div className="h-12 w-full animate-pulse rounded-m bg-icoGray-100 dark:bg-icoDarkMode-deepBlack"></div>
            <div className="h-12 w-full animate-pulse rounded-m bg-icoGray-100 dark:bg-icoDarkMode-deepBlack"></div>
        </div>
    </div>
);

export default BlogPosts;
